import {
  FloatingFocusManager,
  FloatingNode,
  FloatingOverlay,
  FloatingPortal,
  useClick,
  useDismiss,
  useFloating,
  useFloatingNodeId,
  useInteractions,
  useRole,
  useTransitionStatus,
} from '@floating-ui/react';
import { useEffect, useState } from 'react';
import { MAX_TAB_INDEX } from '~/common/hooks';
import { cx, array } from '~/common/utils';
import { FloatingDiv, outsidePress, withFloatingTree } from '../Floating';
import { useModalStackInternal } from './context';
import { usePreserveScroll } from './usePreserveScroll';
import styles from './ModalCore.module.scss';

type ModalItemProps = {
  id: string;
  isVisible: boolean;
};

/**
 * ModalItem - Modal always mounted in DOM
 * Visibility controlled via opacity and pointer-events
 *
 * Features:
 * - All modals mounted, not conditionally rendered
 * - Opacity transitions for smooth crossfade
 * - Previous modal visible while current modal closes
 */
const ModalItem = ({ id, isVisible }: ModalItemProps) => {
  const { stack, closingId, cleanupClosing, closeModals, dismissHandlerData } =
    useModalStackInternal();
  const nodeId = useFloatingNodeId();
  const scrollRef = usePreserveScroll(id);

  const modal = stack.find((modal) => modal.id === id);
  const isClosing = closingId === id;

  const { refs, context } = useFloating({
    nodeId,
    open: isVisible,
    onOpenChange: (state) => {
      if (state) {
        return;
      }
      // Use custom dismiss handler if registered for this modal
      if (dismissHandlerData?.id === id) {
        dismissHandlerData.cb();
      } else {
        closeModals(1);
      }
    },
  });

  const { getFloatingProps } = useInteractions([
    useClick(context),
    useRole(context),
    useDismiss(context, {
      outsidePress,
    }),
  ]);

  const { status } = useTransitionStatus(context, {
    duration: {
      open: 300,
      close: 100,
    },
  });

  return (
    <FloatingNode id={nodeId}>
      <FloatingFocusManager context={context} initialFocus={MAX_TAB_INDEX} disabled={!isVisible}>
        <div
          ref={scrollRef}
          className={cx(
            'fixed inset-0 grid place-items-center z-50 smo:items-end max-h-[calc(var(--dvh))] overflow-auto',
            isVisible ? 'pointer-events-auto' : 'pointer-events-none ',
          )}
        >
          <div ref={refs.setReference}>
            <FloatingDiv
              {...getFloatingProps({
                ref: refs.setFloating,
                className: cx(styles.modal, 'focus-visible:outline-none'),
              })}
              data-status={status}
              onTransitionEnd={(e) => {
                // Only handle 1 transition end (for example opacity) to avoid duplicate calls
                if (e.target === e.currentTarget && e.propertyName === 'opacity' && isClosing) {
                  cleanupClosing();
                }
              }}
            >
              {modal && (
                <modal.component
                  {...modal.props}
                  onClose={() => {
                    modal.props?.onClose?.();
                    closeModals(1);
                  }}
                />
              )}
            </FloatingDiv>
          </div>
        </div>
      </FloatingFocusManager>
    </FloatingNode>
  );
};

export const ModalStackCore = withFloatingTree(() => {
  const { stack, closingId, opened } = useModalStackInternal();
  const [backdropStatus, setBackdropStatus] = useState<'initial' | 'open' | 'close'>('initial');

  // Update backdrop visibility when stack changes

  useEffect(() => {
    // Backdrop is open if there are modals in the stack
    const hasModals = stack.length > 0;
    const isLastModalClosing = closingId === stack[stack.length - 1]?.id && stack.length <= 1;

    if (hasModals && backdropStatus === 'initial') {
      // Force frame delay: initial → open (triggers animation)
      requestAnimationFrame(() => {
        setBackdropStatus('open');
      });
    } else if (isLastModalClosing) {
      // Direct transition: open → close
      setBackdropStatus('close');
    } else if (!hasModals) {
      // Reset: close → initial (when stack is completely empty)
      setBackdropStatus('initial');
    }
  }, [stack, backdropStatus, closingId]);

  // Get the visible modal (last one that's not closing)
  const openedModal = array.findLast(stack, (m) => m.id !== closingId)?.id ?? null;

  if (!opened) return null;

  return (
    <FloatingPortal>
      <FloatingOverlay lockScroll />

      <div className={cx(styles.backdrop)} data-status={backdropStatus}></div>

      {stack.map((modal) => (
        <ModalItem
          key={modal.id}
          id={modal.id}
          isVisible={modal.id === openedModal && closingId !== modal.id}
        />
      ))}
    </FloatingPortal>
  );
});
